import { HttpResponse } from "msw";
import type {
  ApiErrorShape,
  Community,
  Creator,
  CreatorSummary,
  Post,
  ProductSummary,
} from "../../api/types";
import { getCollection } from "../db";
import { delay, delayLatency, getSettings, MockMutationError } from "../latency";
import * as viewerState from "../viewer-state";
import { productCompareAtPrice } from "@/lib/product-sale";
import { VIEWER_USERNAME } from "../viewer-state";

/**
 * Wraps a mock response body with the plan §4.4 latency/failure simulation.
 * GETs never fail (`mutation: false`, the default); mutations roll a
 * simulated 500 at the configured `failMutations` rate.
 */
export async function mockJson<T>(value: T, opts: { mutation?: boolean; status?: number } = {}): Promise<Response> {
  try {
    await delay(opts.mutation ?? false);
    return HttpResponse.json(value as never, { status: opts.status ?? 200 });
  } catch (error) {
    if (error instanceof MockMutationError) {
      const body: ApiErrorShape = { status: 500, message: error.message };
      return HttpResponse.json(body, { status: 500 });
    }
    throw error;
  }
}

export async function runMutation<T>(apply: () => T | Promise<T>, opts: { status?: number } = {}): Promise<Response> {
  await delayLatency();
  if (Math.random() < getSettings().failMutations) {
    const body: ApiErrorShape = { status: 500, message: "Simulated mutation failure" };
    return HttpResponse.json(body, { status: 500 });
  }
  return HttpResponse.json((await apply()) as never, { status: opts.status ?? 200 });
}

export function notFound(message = "Not found"): Response {
  const body: ApiErrorShape = { status: 404, message };
  return HttpResponse.json(body, { status: 404 });
}

export function conflict(message: string): Response {
  const body: ApiErrorShape = { status: 409, message };
  return HttpResponse.json(body, { status: 409 });
}

// --- Viewer-relative decoration ---------------------------------------------
//
// Every response passes entities through these functions so a mutation
// (follow, like, join, cart add, …) recorded once in `viewer-state` is
// reflected consistently no matter which collection/endpoint the entity is
// read back from.

export function decorateCreatorSummary(summary: CreatorSummary): CreatorSummary {
  if (summary.username === VIEWER_USERNAME) {
    const overrides = viewerState.profileOverrides();
    return { ...summary, displayName: overrides.displayName ?? summary.displayName, isFollowing: false };
  }
  return { ...summary, isFollowing: viewerState.isFollowingCreator(summary.username) };
}

export function decorateCreator(creator: Creator): Creator {
  const isSelf = creator.username === VIEWER_USERNAME;
  if (isSelf) {
    const overrides = viewerState.profileOverrides();
    return { ...creator, ...overrides, isFollowing: false };
  }
  return {
    ...creator,
    isFollowing: viewerState.isFollowingCreator(creator.username),
    followers: creator.followers + viewerState.followerDelta(creator.username),
  };
}

export function decorateProductSummary(product: ProductSummary): ProductSummary {
  return {
    ...product,
    // Derived per request rather than stored: `price` is what the buyer pays, and
    // the original it was discounted from is presentation the API supplies.
    compareAtPrice: productCompareAtPrice(product.id, product.price),
    seller: decorateCreatorSummary(product.seller),
  };
}

export function decoratePost(post: Post): Post {
  return {
    ...post,
    author: decorateCreatorSummary(post.author),
    product: post.product ? decorateProductSummary(post.product) : undefined,
    liked: viewerState.isPostLiked(post.id),
    bookmarked: viewerState.isPostBookmarked(post.id),
    likes: post.likes + viewerState.likeDelta(post.id),
  };
}

export function decorateCommunity(community: Community): Community {
  return {
    ...community,
    joined: viewerState.isCommunityJoined(community.slug),
    members: community.members + viewerState.memberDelta(community.slug),
    moderators: community.moderators.map(decorateCreatorSummary),
  };
}

// --- Shared lookups -----------------------------------------------------

export function toCreatorSummary(creator: Creator): CreatorSummary {
  return {
    id: creator.id,
    username: creator.username,
    displayName: creator.displayName,
    avatar: creator.avatar,
    verified: creator.verified,
    role: creator.role,
  };
}

export async function findViewerCreator(): Promise<Creator> {
  const creators = await getCollection<Creator>("creators");
  const me = creators.find((c) => c.username === VIEWER_USERNAME);
  if (!me) throw new Error("Seed data is missing the viewer creator — run `npm run seed`.");
  return me;
}

export async function findViewerStoreSlug(): Promise<string> {
  const me = await findViewerCreator();
  return me.storeSlug ?? VIEWER_USERNAME;
}

/** Every post visible this session: the viewer's freshly created posts, then the seeded set. */
export async function allPosts(): Promise<Post[]> {
  const seeded = await getCollection<Post>("posts");
  return [...viewerState.createdPosts(), ...seeded];
}

export function parsePositiveInt(value: string | null, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
